import { FC, useState } from 'react';
import { Modal } from '../components/Modal';
import { IonCardContent, IonContent, IonSkeletonText, IonToggle, ToggleChangeEventDetail } from '@ionic/react';
import { isPlatform } from '../utils/isPlatform';
import { IonToggleCustomEvent } from '@ionic/core';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { INotificationKey, INotificationType, UserSettingsState } from './UserSettingsState';
import { useSubscription } from '@meraki-internal/state';
import { createUseStyles } from 'react-jss';
import { Text } from '../components/Text';
import { NudgeBadge } from '../components/NudgeBadge';
import { PushNotificationService } from '../push-notifications/PushNotificationService';
import { AlertPresenter } from '../AlertBinder';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { mediaMinWidth } from '../theme/utils';
import { useBreakpoints } from '../theme/useBreakpoints';

const useStyles = createUseStyles({
    section: {
        paddingBottom: 32,

        // toggle description text
        '& ion-text:nth-child(2)': {
            marginTop: 5,
            lineHeight: '20px',
            fontSize: 15
        },

        // 2-col layout on larger screens
        [mediaMinWidth.sm]: {
            width: '100%',
            display: 'flex',
            gap: 30
        }
    },

    toggleRow: {
        marginTop: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end'
    }
});

export type INotificationSettingsProps = {
    isOpen?: boolean;
    closeHandler: () => void;
};

export const NotificationSettingsModal: FC<INotificationSettingsProps> = ({ isOpen = false, closeHandler }) => {
    const classes = useStyles();
    const isDesktop = isPlatform('desktop');

    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const userSettingsState = useInstance(UserSettingsState);
    useSubscription(() => userSettingsState);

    const alertPresenter = useInstance(AlertPresenter);
    const pushNotificationService = useInstance(PushNotificationService);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const { isAboveBreakpoint } = useBreakpoints();

    const toggleLabels = {
        'email': 'Email',
        'push': isAboveBreakpoint('sm') ? 'Push' : 'Push Notification'
    };

    const loadingSkeletonSizes = isAboveBreakpoint('sm') ? [1, 1, 1, 1] : [3, 2, 2, 3];

    const loadSettings = async () => {
        setIsLoaded(false);
        await userSettingsState.load();
        setIsLoaded(true);
        mixpanel.track('Notification Settings Opened');
    };

    const settingChanged = async (key: INotificationKey, attr: INotificationType, value: boolean) => {
        const status = await pushNotificationService.checkPermissions();
        if (!isPlatform('desktop') && value === true && status !== 'granted') {
            if (status === 'prompt') {
                const requestStatus = await pushNotificationService.requestPermission();
                if (requestStatus === 'granted') {
                    userSettingsState.updateNotificationSetting(key, attr, value);
                    userSettingsState.save();
                }
            } else if (status === 'denied') {
                // don't save and disable the setting since notifications are not enabled
                userSettingsState.updateNotificationSetting(key, attr, false);

                let subHeader = '';
                let message = '';
                if (isPlatform('ios')) {
                    subHeader = 'Please enable Notifications for Innerhive in the Settings app.';
                    message = 'Click "Open Settings" below, then select Notifications > Innerhive, and enable "Allow Notifications".';
                } else if (isPlatform('android')) {
                    subHeader = 'Please enable Notifications for Innerhive in Settings.';
                    message = 'Click "Open Settings" below, and allow notifications for Innerhive.';
                }
                const action = await alertPresenter.showAlertV2({
                    header: 'Notifications Disabled',
                    subHeader: subHeader,
                    message: message,
                    backdropDismiss: true,
                    options: [{
                        id: 'cancel',
                        label: 'No Thanks',
                        role: 'cancel'
                    }, {
                        id: 'continue',
                        label: 'Open Settings'
                    }]
                });

                if (action === 'continue') {
                    NativeSettings.open({ optionIOS: IOSSettings.App, optionAndroid: AndroidSettings.ApplicationDetails });
                }
            }
        } else {
            userSettingsState.updateNotificationSetting(key, attr, value);
            userSettingsState.save();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            desktopMaxWidth={535}
            title="Notification Settings"
            data-id="notification-settings-modal"
            closeHandler={closeHandler}
            onDidPresent={loadSettings}
        >
            <IonContent data-id="notification-settings-form">
                <IonCardContent style={{paddingLeft: 22, paddingRight: 22}}>
                    {isDesktop && (
                        <div style={{marginTop: -35}}>
                            <NudgeBadge
                                excludeIcon
                                allowAsViewer
                                color="success"
                                message="To receive notifications, please download the Innerhive mobile app!"
                            />
                        </div>
                    )}
                    {isLoaded ? (
                        <>
                            <div className={classes.section}>
                                <div>
                                    <Text type="subtitle">Community Updates</Text>
                                    <Text>Get updates for new posts, relevant comments, and public notes to stay in the loop. Private notes will not trigger notifications.</Text>
                                </div>
                                <div className={classes.toggleRow}>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                                        <IonToggle
                                            data-id="community-email-toggle"
                                            data-value={!userSettingsState.state?.notifications.community?.emailDisabled}
                                            checked={!userSettingsState.state?.notifications.community?.emailDisabled}
                                            onIonChange={(e: IonToggleCustomEvent<ToggleChangeEventDetail<any>>) => {
                                                settingChanged('community', 'email', e.detail.checked);
                                            }}
                                        >
                                            {toggleLabels.email}
                                        </IonToggle>
                                        <IonToggle
                                            data-id="community-push-toggle"
                                            data-value={!userSettingsState.state?.notifications.community?.pushDisabled}
                                            checked={!userSettingsState.state?.notifications.community?.pushDisabled}
                                            onIonChange={(e: IonToggleCustomEvent<ToggleChangeEventDetail<any>>) => {
                                                settingChanged('community', 'push', e.detail.checked);
                                            }}
                                        >
                                            {toggleLabels.push}
                                        </IonToggle>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.section}>
                                <div>
                                    <Text type="subtitle">Care Updates</Text>
                                    <Text>Get alerts for changes in care, including updates to diagnoses, allergies, scheduled appointments, and new support members.</Text>
                                </div>
                                <div className={classes.toggleRow}>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                                        <IonToggle
                                            data-id="care-push-toggle"
                                            data-value={!userSettingsState.state?.notifications.care?.pushDisabled}
                                            checked={!userSettingsState.state?.notifications.care?.pushDisabled}
                                            onIonChange={(e: IonToggleCustomEvent<ToggleChangeEventDetail<any>>) => {
                                                settingChanged('care', 'push', e.detail.checked);
                                            }}
                                        >
                                            {toggleLabels.push}
                                        </IonToggle>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.section}>
                                <div>
                                    <Text type="subtitle">Tips &amp; Inspiration</Text>
                                    <Text>Stay motivated with weekly tips and support for your caregiving journey.</Text>
                                </div>
                                <div className={classes.toggleRow}>
                                    <IonToggle
                                        data-id="scheduled-toggle"
                                        data-value={userSettingsState.state?.notifications.scheduledTips.enabled}
                                        checked={userSettingsState.state?.notifications.scheduledTips.enabled}
                                        onIonChange={(e: IonToggleCustomEvent<ToggleChangeEventDetail<any>>) => {
                                            settingChanged('scheduledTips', 'push', e.detail.checked);
                                        }}
                                    >
                                        {toggleLabels.push}
                                    </IonToggle>
                                </div>
                            </div>

                            <div className={classes.section}>
                                <div>
                                    <Text type="subtitle">Daily Care Journal Reminders</Text>
                                    <Text>Receive a gentle reminder each evening to log how your day went. Discover how daily reflections can enhance your wellness!</Text>
                                </div>
                                <div className={classes.toggleRow}>
                                    <IonToggle
                                        data-id="daily-toggle"
                                        data-value={userSettingsState.state?.notifications.dailyLog.enabled}
                                        checked={userSettingsState.state?.notifications.dailyLog.enabled}
                                        onIonChange={(e: IonToggleCustomEvent<ToggleChangeEventDetail<any>>) => {
                                            settingChanged('dailyLog', 'push', e.detail.checked);
                                        }}
                                    >
                                        {toggleLabels.push}
                                    </IonToggle>
                                </div>
                            </div>

                            <div className={classes.section}>
                                <div>
                                    <Text type="subtitle">Task Review</Text>
                                    <Text>Review your care tasks with gentle reminders. Stay on top of those to-do’s and care with confidence!</Text>
                                </div>
                                <div className={classes.toggleRow}>
                                    <IonToggle
                                        data-id="daily-tasks-toggle"
                                        data-value={userSettingsState.state?.notifications.dailyTasks?.enabled ?? true}
                                        checked={userSettingsState.state?.notifications.dailyTasks?.enabled ?? true}
                                        onIonChange={(e: IonToggleCustomEvent<ToggleChangeEventDetail<any>>) => {
                                            settingChanged('dailyTasks', 'push', e.detail.checked);
                                        }}
                                    >
                                        {toggleLabels.push}
                                    </IonToggle>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {loadingSkeletonSizes.map((length, i) => (
                                <div key={i} style={{marginTop: 30, marginBottom: 60}}>
                                    <div style={{display: 'flex', width: '100%'}}>
                                        <IonSkeletonText animated style={{'--border-radius': '999px', width: '90%', height: 20, marginRight: 10}}></IonSkeletonText>
                                        <IonSkeletonText animated style={{'--border-radius': '999px', width: '10%', height: 20}}></IonSkeletonText>
                                    </div>
                                    {Array.from({length}).map((_, j) => (
                                        <IonSkeletonText key={j} animated style={{'--border-radius': '999px', width: '100%', height: 20, marginRight: 10}}></IonSkeletonText>
                                    ))}
                                </div>
                            ))}
                        </>
                    )}
                </IonCardContent>
            </IonContent>
        </Modal>
    );
};
